import { useEffect, useState, useCallback } from "react";
import { Star, Loader2, MessageSquare, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6", GREEN = "#1E7E45";

const STATUS_COLOR = {
  published: { color: GREEN, bg: "rgba(30,126,69,.10)" },
  pending: { color: "#B7791F", bg: "rgba(216,154,0,.14)" },
  hidden: { color: SLATE, bg: "rgba(119,127,136,.12)" },
  rejected: { color: "#DC2626", bg: "rgba(220,38,38,.10)" },
};
const TAB_KEYS = [["", "tab_all"], ["published", "tab_published"], ["pending", "tab_pending"]];
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

function Stars({ n }) {
  return (
    <span className="inline-flex" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={15} style={{ color: i <= n ? GOLD : "#DAD6CC" }} fill={i <= n ? GOLD : "none"} />)}
    </span>
  );
}

export default function Bewertungen() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("");
  const [myRank, setMyRank] = useState(0);
  const [locs, setLocs] = useState([]);
  const [multi, setMulti] = useState(false);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [saving, setSaving] = useState(false);

  const canManage = myRank >= 3;
  const locName = (id) => (id ? (locs.find((l) => l.id === id)?.name || t("common.location")) : t("common.all_locations"));

  const load = useCallback((status) => {
    setLoading(true);
    const q = status ? `?status=${status}` : "";
    api.get(`/reviews${q}`).then(({ data }) => setItems(data.reviews || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(tab); }, [load, tab]);
  useEffect(() => {
    api.get("/org/current").then(({ data }) => { if (data.status === "ok") setMyRank({ owner: 4, admin: 3, editor: 2, employee: 1 }[data.tenant.role] || 0); }).catch(() => {});
    api.get("/me/locations").then(({ data }) => { setLocs(data.locations || []); setMulti(!!data.multi); }).catch(() => {});
  }, []);

  const openReply = (r) => { setReplyFor(r); setReplyText(r.reply || ""); };
  const close = () => { setReplyFor(null); setReplyText(""); };
  const saveReply = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put(`/reviews/${replyFor.id}/reply`, { reply: replyText.trim() }); toast.success(t("reviews.toast_saved")); close(); load(tab); }
    catch (e2) { toast.error(e2.response?.data?.detail || t("reviews.err_save")); }
    finally { setSaving(false); }
  };

  const avg = items.length ? (items.reduce((a, r) => a + (r.rating || 0), 0) / items.length).toFixed(1) : "–";

  return (
    <CockpitLayout active="bewertungen" title={t("reviews.title")}>
      <div className="fundio-page-stack" data-testid="gold-bewertungen">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" style={{ color: INK }} data-testid="reviews-title">
              <Star size={28} style={{ color: GOLD }} /> {t("reviews.title")}
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">{t("reviews.subtitle")}</p>
          </div>
        </div>

        {!loading && items.length > 0 && (
          <div className="fundio-panel inline-flex w-fit items-center gap-3" data-testid="reviews-summary">
            <span className="text-[28px] font-black tabular-nums" style={{ color: INK }}>{avg}</span>
            <span><Stars n={Math.round(avg)} /><span className="mt-0.5 block text-[12px]" style={{ color: SLATE }}>{t("reviews.count", { count: items.length })}</span></span>
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="reviews-tabs">
          {TAB_KEYS.map(([val, key]) => (
            <button key={val || "all"} data-testid={`reviews-tab-${val || "all"}`} onClick={() => setTab(val)}
              className="rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors"
              style={tab === val ? { background: GOLD, color: INK } : { border: `1px solid ${LINE}`, color: SLATE, background: "#fff" }}>{t(`reviews.${key}`)}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : items.length === 0 ? (
          <div className="fundio-panel p-10 text-center" style={{ borderStyle: "dashed" }} data-testid="reviews-empty">
            <Star size={30} className="mx-auto" style={{ color: LINE }} />
            <p className="mt-3 text-[15px] font-semibold" style={{ color: INK }}>{t("reviews.empty_title")}</p>
            <p className="mt-1 text-[13px]" style={{ color: SLATE }}>{t("reviews.empty_body")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="reviews-list">
            {items.map((r) => {
              const st = STATUS_COLOR[r.status] || STATUS_COLOR.pending;
              const initials = (r.author_name || "?").replace(/[^A-Za-zÀ-ÿ ]/g, "").split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?";
              return (
                <div key={r.id} className="fundio-panel" data-testid={`review-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white" style={{ background: GOLD }}>{initials}</span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-[15px] font-bold" style={{ color: INK }}>{r.author_name || t("reviews.anonymous")}</h3>
                          <Stars n={r.rating || 0} />
                        </div>
                        {r.title && <p className="mt-0.5 text-[14px] font-semibold" style={{ color: INK }}>{r.title}</p>}
                        {r.text && <p className="mt-1 text-[13.5px] leading-relaxed" style={{ color: MUTED }}>{r.text}</p>}
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]" style={{ color: SLATE }}>
                          <span>{fmtDate(r.created_at)}</span>
                          {r.source && <span>{t("reviews.source", { source: r.source })}</span>}
                          {multi && <span className="flex items-center gap-1"><MapPin size={12} /> {locName(r.location_id)}</span>}
                        </div>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase" style={{ background: st.bg, color: st.color }} data-testid={`review-status-${r.id}`}>{t(`reviews.status_${r.status}`)}</span>
                  </div>

                  {r.reply ? (
                    <div className="mt-3 rounded-lg border-l-2 p-3" style={{ borderColor: GOLD, background: "rgba(216,154,0,.06)" }} data-testid={`review-reply-${r.id}`}>
                      <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: GOLD }}>{t("reviews.your_reply")}</p>
                      <p className="mt-1 text-[13.5px]" style={{ color: MUTED }}>{r.reply}</p>
                    </div>
                  ) : null}

                  {canManage && (
                    <div className="mt-4 border-t pt-3" style={{ borderColor: LINE }}>
                      <button data-testid={`review-reply-btn-${r.id}`} onClick={() => openReply(r)} className="fundio-action" style={{ minHeight: 34, padding: "6px 12px", fontSize: 13 }}>
                        <MessageSquare size={14} /> {r.reply ? t("reviews.reply_edit") : t("reviews.reply")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {replyFor && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" data-testid="review-reply-editor">
          <div className="fundio-panel w-full max-w-lg rounded-t-2xl sm:rounded-2xl">
            <div className="flex items-center justify-between">
              <h2 className="text-[18px] font-bold" style={{ color: INK }}>{t("reviews.reply_title", { name: replyFor.author_name || t("reviews.title") })}</h2>
              <button onClick={close} data-testid="review-reply-close" className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-fundio-subtle" style={{ color: SLATE }}><X size={18} /></button>
            </div>
            <form onSubmit={saveReply} className="mt-4 space-y-3">
              <textarea data-testid="review-reply-input" value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={4} className="fundio-input" placeholder={t("reviews.reply_ph")} />
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={close} className="fundio-action">{t("common.cancel")}</button>
                <button type="submit" data-testid="review-reply-submit" disabled={saving} className="fundio-action fundio-action--gold">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : null} {t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CockpitLayout>
  );
}
